import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { localSchedulesService } from '@/services';
import type { ApiLocalSchedule } from '@/types/api';
import { useToast } from '@/hooks/use-toast';

interface LocalScheduleDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schedule: ApiLocalSchedule;
  controllerId: string;
}

export function LocalScheduleDeleteDialog({
  open,
  onOpenChange,
  schedule,
  controllerId,
}: LocalScheduleDeleteDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const response = await localSchedulesService.deleteById(schedule.id, controllerId);
      if (!response.success) {
        const msg = response.error?.message ?? (response as { message?: string }).message;
        throw new Error(msg || 'Error al eliminar programación');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['local-schedules', controllerId] });
      toast({
        title: 'Programación eliminada',
        description: 'Eliminada de la plataforma y del dispositivo.',
      });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Eliminar Programación Local</DialogTitle>
          <DialogDescription>
            ¿Eliminar la programación "{schedule.name || `Slot ${schedule.slotNumber}`}"? Se eliminará también del dispositivo. Esta acción no se puede deshacer.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
